/**
 * src/components/map/AgentTeleportHUD.tsx
 *
 * Renders the two visible surfaces of "AI Agent Teleport Mode":
 *
 *   1. The selection panel — a small floating card the user sees
 *      after clicking an AI-agent marker but before confirming the
 *      teleport. Shows the agent's short label, its country if we
 *      know it, the next queued destination if any, and three
 *      actions: Start Teleport, Open Profile (delegated up), Cancel.
 *
 *   2. The active HUD — visible while teleport mode is on. Houses
 *      the phase badge, current-stop counter, countdown, manual-
 *      override toggle (Follow Agent / Free Look) and the persistent
 *      Exit button.
 *
 * The component is purely presentational — it consumes the
 * `AgentTeleportApi` returned by `useAgentTeleportMode` and emits
 * no map calls of its own. That keeps every camera move concentrated
 * inside the hook (so animation safety / cleanup is one place).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type mapboxgl from 'mapbox-gl';

import type { AgentTeleportApi, TeleportPhase } from '../../hooks/useAgentTeleportMode';
import {
  suggestPlaces,
  type TeleportPlaceSuggestion,
} from '../../services/teleportPlaceSearch';
import AgentTeleportLivePlayer from './AgentTeleportLivePlayer';
import type { LiveSession } from '../../services/liveSessionService';
import { getAgentBalances } from '../../services/agentService';
import {
  getAgentNearby,
  type AgentNearbyResult,
} from '../../services/agentPositionService';
import { haversineDistanceKm } from '../../utils/mapAnimation';
import { geoLinkSpatialObjectsAdminClient } from '../../services/spatial/geoLinkSpatialObjectsAdminClient';
import {
  haversineMeters,
  rowCenter,
  spatialTypeMeta,
  formatDistance,
} from '../../services/spatial/spatialProximity';
import AgentBroadcastCanvas from '../AgentBroadcastCanvas';
import { useAgentAutoBroadcast } from '../../hooks/useAgentAutoBroadcast';
import { iso2ToNumeric } from '../../countryCodes';
import type { ExecuteLivePayment } from '../ProfileOverlay';
import { generateNarration } from '../../services/agentNarrationService';

const PHASE_LABEL: Record<TeleportPhase, string> = {
  idle:      'Idle',
  entering:  'Entering…',
  flying:    'Teleporting…',
  arrived:   'Arrived',
  rotating:  'Looking around',
  countdown: 'Next teleport',
  exiting:   'Restoring view',
};

const PHASE_COLOR: Record<TeleportPhase, string> = {
  idle:      '#94a3b8',
  entering:  '#6366f1',
  flying:    '#8b5cf6',
  arrived:   '#10b981',
  rotating:  '#0ea5e9',
  countdown: '#f59e0b',
  exiting:   '#94a3b8',
};

function shortPk(pk: string | null | undefined): string {
  if (!pk) return '—';
  return `${pk.slice(0, 4)}…${pk.slice(-4)}`;
}

// One-time CSS injection for the role-change toast slide-in. Done
// here (not in a global stylesheet) so the HUD is fully self-
// contained — anyone who imports it gets the animation for free.
let _animCssInstalled = false;
function _installAnimCss() {
  if (_animCssInstalled || typeof document === 'undefined') return;
  _animCssInstalled = true;
  const style = document.createElement('style');
  style.setAttribute('data-gt-teleport-hud-anim', 'true');
  style.textContent = `
    @keyframes gt-teleport-toast-in {
      0%   { opacity: 0; transform: translate(-50%, -8px); }
      100% { opacity: 1; transform: translate(-50%, 0);    }
    }
  `;
  document.head.appendChild(style);
}

const cardStyle: React.CSSProperties = {
  position:      'absolute',
  zIndex:        2200,           // above markers, popups; below modals
  background:    'rgba(15, 23, 42, 0.94)',
  color:         '#e2e8f0',
  borderRadius:  12,
  padding:       '12px 14px',
  boxShadow:     '0 6px 24px rgba(0, 0, 0, 0.45)',
  border:        '1px solid rgba(99, 102, 241, 0.4)',
  font:          '500 12px/1.4 system-ui, -apple-system, sans-serif',
  pointerEvents: 'auto',
};

const buttonBase: React.CSSProperties = {
  border:        '1px solid rgba(148, 163, 184, 0.3)',
  background:    'rgba(30, 41, 59, 0.85)',
  color:         '#e2e8f0',
  borderRadius:  8,
  padding:       '6px 10px',
  font:          '600 12px system-ui, -apple-system, sans-serif',
  cursor:        'pointer',
};

const primaryBtn: React.CSSProperties = {
  ...buttonBase,
  background:  'linear-gradient(135deg, #6366f1, #8b5cf6)',
  borderColor: 'transparent',
  color:       '#ffffff',
};

const dangerBtn: React.CSSProperties = {
  ...buttonBase,
  background:  'rgba(239, 68, 68, 0.18)',
  borderColor: 'rgba(239, 68, 68, 0.45)',
  color:       '#fca5a5',
};

export interface AgentTeleportHUDProps {
  api: AgentTeleportApi;
  /**
   * Optional bridge for the "Open Profile" action so the parent can
   * keep using its existing wallet-profile flow rather than us
   * inventing a new one. When omitted, the button is hidden.
   */
  onOpenProfile?: (agentPk: string) => void;
  /**
   * Active live broadcast for the agent the user is currently
   * following — when set, the HUD mounts an inline mini player so
   * the user can watch + listen to the agent without leaving
   * teleport mode. Computed by the parent from the always-on live
   * feed (see App.tsx where this is derived from
   * `liveMarkerFeed.sessions.find(...)`).
   */
  followedAgentLiveSession?: LiveSession | null;
  /**
   * Viewer's wallet — forwarded to the SFU when the mini player
   * joins. Optional; the SFU treats viewers as anonymous when
   * absent.
   */
  viewerWallet?: string | null;
  /**
   * The host's main Mapbox GL map instance. When provided AND a
   * live broadcast is active for the followed agent, the inline
   * mini player renders a SECOND tiny Mapbox canvas alongside the
   * agent's video that mirrors this map's camera (split-pane PIP).
   *
   * For viewers in a watch party, this works for free because
   * teleportHub is already syncing the camera state into their
   * local map — the mini canvas just reflects whatever the local
   * map shows. No second WebRTC broadcast is started.
   *
   * Mapbox style URL is forwarded so the mini map matches the
   * host's currently-active style (dark, satellite, etc.) when
   * the host changes it via the HUD's style chips.
   */
  hostMap?: mapboxgl.Map | null;
  /**
   * Mapbox style URL the host map is currently using — passed
   * separately because mapboxgl.Map doesn't expose the style URL
   * back to React idiomatically. The mini map uses this to stay
   * in sync when the host swaps styles. Falls back to streets-v12
   * if not provided.
   */
  hostMapStyle?: string | null;
  /**
   * The full always-on live-broadcast feed (every active
   * `LiveSession`). The HUD uses this to flag OTHER broadcasters
   * who are streaming within range of the agent's current stop —
   * "someone is broadcasting live in the scene" — distinct from the
   * followed agent's own broadcast (`followedAgentLiveSession`).
   * Optional / `null` when teleport isn't active. Each session
   * carries a quantised (~11 km) `approxLat`/`approxLng`, so the
   * proximity test uses a generous radius to account for the
   * privacy rounding.
   */
  liveSessions?: LiveSession[] | null;
  /**
   * Consented wallet payment handler (the same one App.tsx wires into
   * the AgentLiveBroadcastModal via `createLiveSessionPaymentHandler`).
   * Used by the opt-in auto-broadcast so a paid live session honours
   * the operator's wallet flow. When omitted, auto-broadcast still
   * works for free (no-payment) regions and otherwise reports that
   * payment is required.
   */
  onExecuteLivePayment?: ExecuteLivePayment;
  /**
   * The viewer's resolved policy country (numeric ISO-3166-1) — the
   * same value App.tsx feeds the AgentLiveBroadcastModal. Live
   * sessions are gated by a per-country WebRTC policy; the agent's
   * own registered country frequently has no policy configured (→
   * 409 webrtc_policy_not_set), so the auto-broadcast prefers this
   * known-configured viewer country and only falls back to the
   * agent's country when it's absent.
   */
  viewerBroadcastCountry?: number | null;
}

export const AgentTeleportHUD: React.FC<AgentTeleportHUDProps> = ({
  api,
  onOpenProfile,
  followedAgentLiveSession = null,
  viewerWallet = null,
  hostMap = null,
  hostMapStyle = null,
  liveSessions = null,
  onExecuteLivePayment,
  viewerBroadcastCountry = null,
}) => {
  _installAnimCss();

  const {
    pendingPanel,
    enabled,
    phase,
    countdownSeconds,
    currentStopIndex,
    manualOverride,
    nextDestination,
    queueLength,
    selectedAgent,
    selectedAgentId,
    role,
    hostWalletPk,
    viewers,
    start,
    exit,
    dismissPanel,
    setManualOverride,
    enqueuePlace,
    enqueueLngLat,
    paused,
    setPaused,
    currentStopName,
    nextStopName,
    setMapStyle,
    originalMapStyle,
    currentMapStyle,
    displayedAgentPosition,
  } = api;

  // Quick-style chip catalogue. Listed in the order they render in
  // the HUD. We expose three: a dark variant (great for night-time
  // 3D buildings), satellite (gives the cinematic a "watching from
  // a chopper" feel), and a "back to whatever the user had before
  // teleport entered" reset chip. The full SettingsOverlay style
  // dropdown remains the place for niche choices (outdoors,
  // navigation, etc.) — these three cover the cinematic-relevant
  // moods without crowding the HUD.
  const STYLE_CHIPS: Array<{
    id:    'dark' | 'satellite' | 'original';
    label: string;
    title: string;
    /** URL resolver — `original` resolves at click time. */
    url:   string | null;
  }> = [
    {
      id:    'dark',
      label: '🌑 Dark',
      title: 'Switch to Mapbox Dark style — excellent contrast for the cinematic 3D buildings.',
      url:   'mapbox://styles/mapbox/dark-v11',
    },
    {
      id:    'satellite',
      label: '🛰 Satellite',
      title: 'Switch to satellite imagery with street labels — gives the cinematic a "from-orbit" feel.',
      url:   'mapbox://styles/mapbox/satellite-streets-v12',
    },
    {
      id:    'original',
      label: '↺ Original',
      title: 'Reset back to whatever Mapbox style was active when you started teleport mode.',
      url:   originalMapStyle,
    },
  ];

  // ── "Add place to queue" affordance state ──────────────────────────
  // Collapsed by default — the user opens it via the "+ Add place"
  // chip in the top pill, types a query, picks an autocomplete
  // suggestion (or hits Enter to use the top match). We surface a
  // transient 4-second toast for the result so failure modes
  // ("not in this country", "no Mapbox token") don't sit forever
  // on the screen demanding to be dismissed manually.
  const [placeOpen,        setPlaceOpen]        = useState(false);
  const [placeQuery,       setPlaceQuery]       = useState('');
  const [placeBusy,        setPlaceBusy]        = useState(false);
  const [placeToast,       setPlaceToast]       = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [placeSuggestions, setPlaceSuggestions] = useState<TeleportPlaceSuggestion[]>([]);
  const [placeFocused,     setPlaceFocused]     = useState(false);
  const placeInputRef    = useRef<HTMLInputElement | null>(null);
  const placeAbortRef    = useRef<AbortController | null>(null);
  const placeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss the place toast.
  useEffect(() => {
    if (!placeToast) return;
    const t = setTimeout(() => setPlaceToast(null), 4000);
    return () => clearTimeout(t);
  }, [placeToast]);

  // Auto-focus the input when the user opens the affordance — saves
  // a tap on mobile and a click on desktop.
  useEffect(() => {
    if (placeOpen) {
      const t = setTimeout(() => { placeInputRef.current?.focus(); }, 50);
      return () => clearTimeout(t);
    }
  }, [placeOpen]);

  // Debounced typeahead: fires 200ms after the user stops typing.
  // Aborts any in-flight request when a newer keystroke arrives so
  // we never get stale-suggestions racing with the latest query.
  // 2-char minimum keeps us from spamming Mapbox with single-letter
  // queries that return nothing useful anyway.
  useEffect(() => {
    if (!placeOpen) return;
    if (placeDebounceRef.current) clearTimeout(placeDebounceRef.current);
    if (placeAbortRef.current)    placeAbortRef.current.abort();
    const q = placeQuery.trim();
    if (q.length < 2) {
      setPlaceSuggestions([]);
      return;
    }
    placeDebounceRef.current = setTimeout(async () => {
      const ac = new AbortController();
      placeAbortRef.current = ac;
      const country = (selectedAgent?.country_code || null);
      const list = await suggestPlaces(q, { country, signal: ac.signal });
      // Guard: only apply if this request wasn't superseded.
      if (placeAbortRef.current !== ac) return;
      setPlaceSuggestions(list);
    }, 200);
    return () => {
      if (placeDebounceRef.current) clearTimeout(placeDebounceRef.current);
    };
  }, [placeQuery, placeOpen, selectedAgent]);

  // Cleanup: abort any in-flight typeahead when the affordance is
  // closed (saves quota on a forgotten "I changed my mind" close).
  useEffect(() => {
    if (placeOpen) return;
    if (placeAbortRef.current) {
      placeAbortRef.current.abort();
      placeAbortRef.current = null;
    }
    setPlaceSuggestions([]);
  }, [placeOpen]);

  const pickSuggestion = (s: TeleportPlaceSuggestion) => {
    if (placeBusy) return;
    enqueueLngLat({ lat: s.lat, lng: s.lng }, s.placeName);
    setPlaceToast({ kind: 'ok', text: `Queued: ${s.placeName}` });
    setPlaceQuery('');
    setPlaceSuggestions([]);
    // Keep the affordance open so the user can chain several picks
    // back-to-back without re-clicking the chip.
    placeInputRef.current?.focus();
  };

  const submitPlace = async () => {
    const q = placeQuery.trim();
    if (!q || placeBusy) return;
    // If there's a top suggestion already loaded, prefer it — saves
    // a redundant cache+geocode round trip and matches what the user
    // is visually about to click anyway.
    if (placeSuggestions.length > 0) {
      pickSuggestion(placeSuggestions[0]);
      return;
    }
    setPlaceBusy(true);
    try {
      const result = await enqueuePlace(q);
      if (result.ok) {
        const sourceLabel =
          result.source === 'geolink_cache' ? 'cached'
            : result.source === 'mapbox'    ? 'searched'
            : 'searched';
        setPlaceToast({
          kind: 'ok',
          text: `Queued (${sourceLabel}): ${result.placeName}`,
        });
        setPlaceQuery('');
        setPlaceSuggestions([]);
      } else {
        setPlaceToast({ kind: 'err', text: result.error });
      }
    } catch (err) {
      setPlaceToast({ kind: 'err', text: 'Could not look up that place — try again.' });
    } finally {
      setPlaceBusy(false);
    }
  };

  // Pre-format a coords-only fallback for the "Arrived" / "Next
  // teleport" labels. Used when reverse-geocoding hasn't (yet)
  // resolved a proper place name.
  function _coordLabel(p: { lat: number; lng: number } | null | undefined): string | null {
    if (!p) return null;
    return `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`;
  }

  // Pending stops EXCLUDING the in-flight target. Shows the user
  // how far behind the agent we've fallen — 0 means "caught up".
  const pendingStops = phase === 'flying' || phase === 'entering'
    ? Math.max(0, queueLength - 1)
    : queueLength;

  // Watch-party: count co-viewers (excluding self / host depending
  // on context). Total occupants = viewers.length; the rest is
  // "everyone else who's also here".
  const otherViewerCount = Math.max(0, viewers.length - 1);

  // ── Agent wallet balance ────────────────────────────────────────────
  // Shows the followed agent's on-chain XLM balance in the control
  // pill so the host (and the viewers, since they see the same HUD)
  // can tell at a glance whether the agent has fuel for its next
  // move / payment. Fetched once on session start, refreshed every
  // 45 s so a recent fund/dispose is reflected without hammering
  // Horizon. Best-effort: any failure (unfunded, 404, network) is
  // silently swallowed and the chip just renders "—". The balance
  // is shown for both classic G… and smart-account C… agents
  // (getAgentBalances handles both).
  const [agentBalance, setAgentBalance] = useState<{
    /** Native XLM as a decimal string (e.g. "4.9999900"). */
    native:  string | null;
    /** True when Horizon reported the account doesn't exist yet. */
    unfunded: boolean;
    /** Address that was queried — used to discard stale fetches. */
    address: string;
  } | null>(null);
  useEffect(() => {
    if (!enabled || !selectedAgentId) {
      setAgentBalance(null);
      return;
    }
    let cancelled = false;
    const addr = selectedAgentId;
    const fetchOnce = async () => {
      try {
        const b = await getAgentBalances(addr);
        if (cancelled) return;
        setAgentBalance({
          native:   b.native?.balance ?? null,
          unfunded: !!b.unfunded,
          address:  addr,
        });
      } catch {
        if (cancelled) return;
        setAgentBalance({ native: null, unfunded: false, address: addr });
      }
    };
    void fetchOnce();
    const handle = setInterval(fetchOnce, 45_000);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [enabled, selectedAgentId]);

  // Pretty-print the balance for the chip — trims trailing zeros
  // beyond two decimals so "4.9999900" reads as "5.00" while
  // "4.1234500" still shows two digits ("4.12") to keep the chip
  // narrow. Stroop precision is irrelevant for a status pill.
  const balanceLabel = (() => {
    if (!agentBalance) return null;
    if (agentBalance.unfunded) return 'unfunded';
    if (agentBalance.native == null) return '—';
    const n = Number(agentBalance.native);
    if (!Number.isFinite(n)) return '—';
    return `${n.toFixed(2)} XLM`;
  })();

  // ── Nearby markers (smart contracts + NFTs) ─────────────────────────
  // Surface what on-chain markers the agent is near — smart contract
  // execution-rule markers and NFT markers — for the WHOLE teleport,
  // not just while parked. The user wants in-scene markers detected
  // "when the teleporting is in progress", so we keep polling through
  // entering / flying too and the panel renders in every phase.
  // Deliberately omits nearby WALLETS per the product brief ("don't
  // announce nearby wallets"); the `kinds=contracts,nfts` query keeps
  // GeoLink from sending wallet data over the wire at all. Cadence is
  // a brisk 6 s while moving so the marker list keeps up with the
  // camera, easing to 20 s only when the host has explicitly paused.
  const [nearby, setNearby] = useState<{
    radius_m:  number;
    contracts: AgentNearbyResult['contracts'];
    nfts:      AgentNearbyResult['nfts'];
    address:   string;
    fetchedAt: number;
  } | null>(null);
  useEffect(() => {
    if (!enabled || !selectedAgentId) {
      setNearby(null);
      return;
    }
    let cancelled = false;
    const addr = selectedAgentId;
    const fetchOnce = async () => {
      try {
        const r = await getAgentNearby(addr, {
          kinds:    ['contracts', 'nfts'],
          radius_m: 750,
        });
        if (cancelled) return;
        setNearby({
          radius_m:  r.radius_m,
          contracts: r.contracts || [],
          nfts:      r.nfts || [],
          address:   addr,
          fetchedAt: Date.now(),
        });
      } catch {
        // Best-effort. A 404 / 502 just leaves whatever snapshot we
        // already had on screen — that's better than flickering the
        // panel away on every transient failure.
      }
    };
    // Poll briskly throughout the cinematic (including flight) so
    // markers are detected "in progress"; only back off when paused.
    const cadence = paused ? 20_000 : 6_000;
    void fetchOnce();
    const handle = setInterval(fetchOnce, cadence);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [enabled, selectedAgentId, paused]);

  // ── Nearby LIVE broadcasters ────────────────────────────────────────
  // Flag other people who are streaming live in the scene the agent
  // is teleporting through. Each LiveSession carries a quantised
  // (~11 km) approx location, so we use a generous 20 km radius for
  // the proximity test — at that rounding a tighter threshold would
  // produce false negatives. We exclude the followed agent's OWN
  // broadcast (already surfaced via the inline mini player) and any
  // session without an approx location. Computed from the agent's
  // currently-displayed cinematic position so it tracks the camera
  // across every stop rather than the agent's last server frame.
  const NEARBY_LIVE_RADIUS_KM = 20;
  const anchor: { lat: number; lng: number } | null =
    displayedAgentPosition
      ?? (selectedAgent ? { lat: selectedAgent.lat, lng: selectedAgent.lng } : null);
  const nearbyLive = (() => {
    if (!enabled || !anchor || !Array.isArray(liveSessions)) return [];
    const out: Array<{
      broadcasterId: string;
      wallet:        string | null;
      distanceKm:    number;
    }> = [];
    for (const s of liveSessions) {
      if (s.status !== 'live') continue;
      if (s.broadcasterId === selectedAgentId) continue;   // the followed agent itself
      if (s.approxLat == null || s.approxLng == null) continue;
      const d = haversineDistanceKm(anchor, { lat: s.approxLat, lng: s.approxLng });
      if (d <= NEARBY_LIVE_RADIUS_KM) {
        out.push({ broadcasterId: s.broadcasterId, wallet: s.wallet, distanceKm: d });
      }
    }
    out.sort((a, b) => a.distanceKm - b.distanceKm);
    return out;
  })();

  // ── Nearby SPATIAL OBJECTS (buildings, events, zones, …) ────────────
  // GeoLink's agent `/nearby` only returns wallets/NFTs/contracts. The
  // richer spatial-object REGISTRY (buildings, floors, zones, events,
  // contract triggers, …) is a separate surface, queried by exact
  // coordinates. As the agent flies through the scene we poll that
  // registry around its precise current position so the HUD can name
  // what it's passing and identify each by type ("Burj Khalifa ·
  // Building", "Expo Stage · Event"). Uses a ref for the live anchor
  // so the interval always queries the latest cinematic position
  // without re-arming the timer on every camera nudge.
  const spatialAnchorRef = useRef<{ lat: number; lng: number } | null>(null);
  spatialAnchorRef.current = anchor;
  const [spatialNearby, setSpatialNearby] = useState<{
    objects: Array<{ id: string; type: string; name: string; distanceM: number }>;
    address: string;
  } | null>(null);
  useEffect(() => {
    if (!enabled || !selectedAgentId) {
      setSpatialNearby(null);
      return;
    }
    let cancelled = false;
    const addr = selectedAgentId;
    const SPATIAL_RADIUS_M = 1500;
    const fetchOnce = async () => {
      const here = spatialAnchorRef.current;
      if (!here) return;
      try {
        const rows = await geoLinkSpatialObjectsAdminClient.listObjects({
          near:  { lng: here.lng, lat: here.lat, radius_meters: SPATIAL_RADIUS_M },
          limit: 40,
        });
        if (cancelled) return;
        const anchorNow = spatialAnchorRef.current || here;
        const objects = rows
          .map((r) => {
            const c = rowCenter(r);
            const distanceM = c ? haversineMeters(anchorNow, c) : Number.POSITIVE_INFINITY;
            return {
              id:        r.id,
              type:      (r.objectType as string) || 'object_3d',
              name:      r.name || r.id.slice(0, 8),
              distanceM,
            };
          })
          .filter((o) => Number.isFinite(o.distanceM) && o.distanceM <= SPATIAL_RADIUS_M)
          .sort((a, b) => a.distanceM - b.distanceM);
        setSpatialNearby({ objects, address: addr });
      } catch {
        // Best-effort — a missing API key / 404 just leaves the prior
        // snapshot (or nothing) rather than flickering the panel.
      }
    };
    const cadence = paused ? 20_000 : 6_000;
    void fetchOnce();
    const handle = setInterval(fetchOnce, cadence);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [enabled, selectedAgentId, paused]);

  // Drop the stale spatial snapshot when the selection changes so the
  // panel never shows the previous agent's surroundings.
  useEffect(() => {
    setSpatialNearby((prev) => (prev && prev.address !== selectedAgentId ? null : prev));
  }, [selectedAgentId]);

  // ── Auto agent broadcast during a viewed teleport ───────────────────
  // Opt-in (host-only toggle). When on, the agent does a short, self-
  // narrated live broadcast — reusing the existing broadcast engine +
  // SFU + TTS + recorder — and the recording is saved to the agent's
  // profile as a past broadcast. We narrate the current stop and the
  // nearby spatial objects we detected above. Only the host can drive
  // it (it spends the operator's wallet on the live session), and it's
  // throttled so it stays an occasional highlight, not a constant stream.
  const isHost = role === 'host' || role == null;
  const remoteViewerCount = Math.max(0, (viewers?.length ?? 0) - 1);
  const agentCountryNum = (() => {
    const iso2 = selectedAgent?.country_code;
    if (!iso2) return null;
    const n = iso2ToNumeric(iso2);
    return n > 0 ? n : null;
  })();
  // Prefer the viewer's known-configured policy country (same one the
  // broadcast modal uses) so the session request doesn't 409 with
  // webrtc_policy_not_set on the agent's unconfigured home country.
  const broadcastCountryNum = viewerBroadcastCountry ?? agentCountryNum;

  // Narration provider — rotates through location + nearby-object
  // lines. Reads the latest values via refs so the long-lived
  // broadcast closure never goes stale.
  const currentStopLabel = currentStopName?.short || null;
  const narrateStateRef = useRef<{
    stopName: string | null;
    country: string | null;
    agentName: string | null;
    objects: Array<{ type: string; name: string }>;
  }>({
    stopName: currentStopLabel,
    country: selectedAgent?.country_code ?? null,
    agentName: selectedAgent?.displayName ?? null,
    objects: spatialNearby?.objects ?? [],
  });
  narrateStateRef.current = {
    stopName: currentStopLabel,
    country: selectedAgent?.country_code ?? null,
    agentName: selectedAgent?.displayName ?? null,
    objects: spatialNearby?.objects ?? [],
  };
  const narrateIdxRef = useRef(0);
  const recentLinesRef = useRef<string[]>([]);

  // Local template line — the guaranteed fallback when the AI
  // assistant is slow / down / unconfigured. Professional PA / transit
  // announcement tone — sparse and factual, never scenic filler.
  const templateNarration = useCallback((intro: boolean): string => {
    const { stopName, objects } = narrateStateRef.current;
    const place = stopName || 'this location';
    if (intro) return `Now arriving at ${place}.`;
    // Continuation announcements only fire when there's a concrete,
    // not-recently-announced point of interest to name. Otherwise stay
    // silent (no scenic filler).
    const fresh = objects.find((o) => !recentLinesRef.current.some((l) => l.includes(o.name)));
    if (fresh) {
      const meta = spatialTypeMeta(fresh.type);
      return `Point of interest ahead: ${fresh.name}, ${meta.label.toLowerCase()}.`;
    }
    return '';
  }, []);

  // AI-driven narration via GeoLink's assistant (Azure AI Foundry),
  // with the template as a hard fallback. Returns null to SKIP a tick
  // when there's nothing notable to announce — keeps the broadcast
  // sparse and professional rather than chatty.
  const buildNarration = useCallback(async (): Promise<string | null> => {
    const idx = narrateIdxRef.current;
    narrateIdxRef.current += 1;
    const intro = idx === 0;
    const { stopName, country, agentName, objects } = narrateStateRef.current;
    // No notable POIs and not the arrival line → say nothing.
    if (!intro && objects.length === 0) return null;
    let line: string;
    try {
      const res = await generateNarration({
        place:       stopName,
        country,
        agentName,
        objects:     objects.map((o) => ({ name: o.name, type: o.type })),
        recentLines: recentLinesRef.current,
        intro,
      }, { timeoutMs: 5000 });
      line = (res.text || '').trim() || templateNarration(intro);
    } catch {
      line = templateNarration(intro);
    }
    line = line.trim();
    if (!line) return null;
    recentLinesRef.current = [...recentLinesRef.current.slice(-3), line];
    return line;
  }, [templateNarration]);

  const autoBroadcast = useAgentAutoBroadcast({
    agentPublicKey:   selectedAgentId,
    displayName:      selectedAgent?.displayName ?? null,
    avatarUrl:        selectedAgent?.avatarUrl ?? null,
    operatorWallet:   isHost ? viewerWallet : null,
    country:          broadcastCountryNum,
    approxLocation:   anchor ? { lat: anchor.lat, lng: anchor.lng } : undefined,
    onExecuteLivePayment,
    narrate:          buildNarration,
    // ~1 minute cast (may run slightly over so the final announcement
    // finishes). Sparse, professional cadence with silence between
    // points of interest instead of constant chatter.
    durationMs:       60_000,
    narrateEveryMs:   14_000,
    title:            currentStopLabel ? `Teleport · ${currentStopLabel}` : 'Teleport broadcast',
  });

  // Host toggle + throttle. Auto-fires a fresh broadcast when the
  // agent ARRIVES somewhere, the session has remote viewers, and the
  // cooldown has elapsed.
  const [autoBroadcastOn, setAutoBroadcastOn] = useState(false);
  const lastBroadcastAtRef = useRef(0);
  const AUTO_BROADCAST_COOLDOWN_MS = 120_000;
  useEffect(() => {
    if (!autoBroadcastOn) return;
    if (!isHost || !viewerWallet || !selectedAgentId) return;
    if (autoBroadcast.status !== 'idle') return;
    if (phase !== 'arrived') return;
    if (remoteViewerCount < 1) return; // only when someone is watching
    if (Date.now() - lastBroadcastAtRef.current < AUTO_BROADCAST_COOLDOWN_MS) return;
    lastBroadcastAtRef.current = Date.now();
    narrateIdxRef.current = 0;
    void autoBroadcast.start();
  }, [autoBroadcastOn, isHost, viewerWallet, selectedAgentId, autoBroadcast, phase, remoteViewerCount]);

  // Stop any in-flight broadcast when teleport mode exits or the
  // toggle is turned off.
  useEffect(() => {
    if ((!enabled || !autoBroadcastOn) && autoBroadcast.status !== 'idle') {
      void autoBroadcast.stop('teleport_or_toggle_off');
    }
  }, [enabled, autoBroadcastOn, autoBroadcast]);

  // Hold the cinematic still while an auto-cast is running so the agent
  // broadcasts from a stationary vantage point rather than "teleporting
  // in motion". Restore the prior pause state when the cast ends.
  const pausedBeforeCastRef = useRef<boolean | null>(null);
  useEffect(() => {
    const castActive =
      autoBroadcast.status === 'starting' ||
      autoBroadcast.status === 'live' ||
      autoBroadcast.status === 'saving';
    if (castActive) {
      if (pausedBeforeCastRef.current === null) {
        pausedBeforeCastRef.current = paused;
        if (!paused) setPaused(true);
      }
    } else if (pausedBeforeCastRef.current !== null) {
      if (!pausedBeforeCastRef.current) setPaused(false);
      pausedBeforeCastRef.current = null;
    }
  }, [autoBroadcast.status, paused, setPaused]);

  const broadcastLive = autoBroadcast.status === 'live';
  const broadcastBusy = autoBroadcast.status === 'starting' || autoBroadcast.status === 'saving';

  // Feed the live teleport map into the broadcast canvas so the MAP is
  // the hero view in both the live cast AND the saved recording (the
  // agent avatar drops to a corner PIP). Wired only while a broadcast
  // is active; cleared otherwise so the engine reverts to avatar-first.
  useEffect(() => {
    const engine = autoBroadcast.engine;
    if (!engine) return;
    if (broadcastLive && hostMap) {
      engine.setBackgroundSource(() => {
        try {
          const c = hostMap.getCanvas();
          return (c && c.width > 0 && c.height > 0) ? c : null;
        } catch { return null; }
      });
      return () => { try { engine.setBackgroundSource(null); } catch { /* ignore */ } };
    }
    engine.setBackgroundSource(null);
  }, [broadcastLive, hostMap, autoBroadcast.engine]);

  // Surface auto-broadcast failures (e.g. the country's WebRTC policy
  // isn't configured) so the host knows WHY nothing went live instead
  // of the chip silently flipping back. Reuses the place toast.
  useEffect(() => {
    if (autoBroadcast.error) {
      setPlaceToast({ kind: 'err', text: autoBroadcast.error });
    }
  }, [autoBroadcast.error]);

  // Explain WHY the wallet/passkey prompt appears (it funds the live
  // session) so signing isn't a mystery. Shown as soon as the hook
  // requests a signature.
  useEffect(() => {
    if (autoBroadcast.notice) {
      setPlaceToast({ kind: 'ok', text: autoBroadcast.notice });
    }
  }, [autoBroadcast.notice]);

  // Drop the stale nearby snapshot the moment a new selection
  // starts loading — otherwise the panel briefly shows the OLD
  // agent's contracts/NFTs after the user switches agents.
  useEffect(() => {
    setNearby((prev) => (prev && prev.address !== selectedAgentId ? null : prev));
  }, [selectedAgentId]);

  // Role-change toast: a tiny 4-second banner that surfaces "you're
  // now the host" / "joined as viewer" / similar transitions. We
  // diff role between renders rather than wiring an event up
  // through the hook because the hook keeps role in React state
  // already and toast UX is purely a HUD concern.
  const prevRoleRef = useRef<string | null>(null);
  const [roleToast, setRoleToast] = useState<string | null>(null);
  useEffect(() => {
    const prev = prevRoleRef.current;
    if (prev !== role) {
      if (prev === 'viewer' && role === 'host') {
        setRoleToast('You are now the host. The watch party is following your camera.');
      } else if (prev === 'host' && role === 'viewer') {
        setRoleToast('Another viewer took over as host.');
      } else if (prev === null && role === 'viewer') {
        setRoleToast(`Joined a watch party in progress${hostWalletPk ? ` (host: ${shortPk(hostWalletPk)})` : ''}.`);
      }
      prevRoleRef.current = role;
    }
    if (!roleToast) return;
    const t = setTimeout(() => setRoleToast(null), 4000);
    return () => clearTimeout(t);
  }, [role, hostWalletPk, roleToast]);

  // ── Selection panel (pre-confirm) ───────────────────────────────────
  // Anchored top-center of the map. We deliberately don't try to
  // anchor it to the marker's screen position — that would require
  // re-projecting on every map move and would also occlude the marker
  // pulse the user is trying to see. Top-center is unambiguous and
  // doesn't fight the map.
  const selectionPanel = pendingPanel && !enabled ? (
    <div
      role="dialog"
      aria-label="Start agent teleport"
      style={{ ...cardStyle, top: 16, left: '50%', transform: 'translateX(-50%)', minWidth: 280 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span aria-hidden style={{ fontSize: 18 }}>🤖</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#a5b4fc' }}>
            {pendingPanel.displayName || shortPk(pendingPanel.agentPk)}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>
            {shortPk(pendingPanel.agentPk)}
            {pendingPanel.countryCode ? `  ·  ${pendingPanel.countryCode}` : ''}
          </div>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismissPanel}
          style={{ ...buttonBase, padding: '2px 8px' }}
        >
          ×
        </button>
      </div>

      <div style={{ fontSize: 11, color: '#cbd5e1', marginBottom: 10 }}>
        Last position: <code style={{ color: '#e2e8f0' }}>
          {pendingPanel.position.lat.toFixed(4)},&nbsp;{pendingPanel.position.lng.toFixed(4)}
        </code>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {onOpenProfile && (
          <button
            type="button"
            onClick={() => { onOpenProfile(pendingPanel.agentPk); dismissPanel(); }}
            style={buttonBase}
          >
            Open profile
          </button>
        )}
        <button
          type="button"
          onClick={dismissPanel}
          style={buttonBase}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => start(pendingPanel.agentPk)}
          style={primaryBtn}
          title="Open a watch-party for this agent. If someone else has already started one, you'll join as a viewer (their camera, queue, and phase) and auto-promote to host if they leave."
        >
          Start Teleport
        </button>
      </div>
    </div>
  ) : null;

  // ── Active teleport HUD ────────────────────────────────────────────
  const activeHUD = enabled ? (
    <>
      {/* Top-center pill: selected agent + phase + camera toggle + exit.
          Always visible while teleporting. The Follow / Free-Look
          toggle lives here (not in a separate bottom-left chip
          anymore) so it's reachable on mobile, where the bottom-
          left corner is covered by the player marker controls and
          the country selector. `maxWidth` clamps the pill to the
          viewport so the cluster of controls wraps cleanly on
          narrow screens instead of blowing out horizontally. */}
      <div
        style={{
          ...cardStyle,
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          minWidth: 280,
          maxWidth: 'calc(100vw - 32px)',
          flexWrap: 'wrap',
        }}
      >
        <span aria-hidden style={{ fontSize: 18 }}>🤖</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#a5b4fc' }}>
            {selectedAgent?.displayName || shortPk(selectedAgentId)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#94a3b8' }}>
            <span
              aria-hidden
              style={{
                display:        'inline-block',
                width:           8,
                height:          8,
                borderRadius:    '50%',
                background:      PHASE_COLOR[phase],
                boxShadow:       `0 0 8px ${PHASE_COLOR[phase]}`,
              }}
            />
            <span>{PHASE_LABEL[phase]}</span>
            <span>·</span>
            <span>Stop&nbsp;{currentStopIndex + 1}</span>
            {pendingStops > 0 && (
              <>
                <span>·</span>
                <span
                  title={`${pendingStops} pending stop${pendingStops === 1 ? '' : 's'} in the queue. The agent is moving faster than the cinematic camera can land — every stop will still be visited in order.`}
                  style={{ color: '#fbbf24' }}
                >
                  +{pendingStops} queued
                </span>
              </>
            )}
            {selectedAgent?.country_code && (
              <>
                <span>·</span>
                <span>{selectedAgent.country_code}</span>
              </>
            )}
            {balanceLabel && (
              <>
                <span>·</span>
                <span
                  title={
                    agentBalance?.unfunded
                      ? "The agent's wallet hasn't been funded on-chain yet."
                      : agentBalance?.native
                        ? `Agent native XLM balance (refreshed every 45 s). Raw: ${agentBalance.native}`
                        : 'Agent wallet balance is unavailable right now (network or Horizon hiccup).'
                  }
                  style={{
                    color:        agentBalance?.unfunded ? '#fca5a5' : '#fde68a',
                    border:       `1px solid ${agentBalance?.unfunded ? 'rgba(239, 68, 68, 0.4)' : 'rgba(251, 191, 36, 0.4)'}`,
                    borderRadius: 999,
                    padding:      '0 6px',
                    fontWeight:   700,
                    fontSize:     10,
                    letterSpacing: 0.3,
                  }}
                >
                  {balanceLabel}
                </span>
              </>
            )}
            {role && (
              <>
                <span>·</span>
                <span
                  title={
                    role === 'host'
                      ? 'You are the host. Other viewers see your camera, queue, and phase in real time.'
                      : `You are mirroring ${hostWalletPk ? shortPk(hostWalletPk) : 'the host'}'s camera. If they exit, the longest-tenured viewer takes over.`
                  }
                  style={{
                    color: role === 'host' ? '#34d399' : '#93c5fd',
                    border: `1px solid ${role === 'host' ? 'rgba(52, 211, 153, 0.4)' : 'rgba(147, 197, 253, 0.4)'}`,
                    borderRadius: 999,
                    padding: '0 6px',
                    fontWeight: 700,
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {role}
                </span>
              </>
            )}
            {otherViewerCount > 0 && (
              <>
                <span>·</span>
                <span
                  title={`${otherViewerCount} other ${otherViewerCount === 1 ? 'person is' : 'people are'} watching this teleport.`}
                  style={{ color: '#cbd5e1' }}
                >
                  +{otherViewerCount} watching
                </span>
              </>
            )}
            {/* Paused badge — host sees their own pause; viewers see
                a "host paused" mirror so they understand why the
                cinematic stalled. Distinguishes pause from
                arrived/rotating which look static for similar
                reasons. */}
            {(paused || (role === 'viewer' && api.hostPaused)) && (
              <>
                <span>·</span>
                <span
                  title={role === 'viewer'
                    ? 'The host has paused the cinematic. The queue continues to grow but the camera will only advance when they resume.'
                    : 'You paused the cinematic. The queue still grows; click Resume to continue advancing through it.'}
                  style={{
                    color:        '#d8b4fe',
                    border:       '1px solid rgba(168, 85, 247, 0.45)',
                    borderRadius: 999,
                    padding:      '0 6px',
                    fontWeight:   700,
                    fontSize:     10,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {role === 'viewer' ? 'Host paused' : 'Paused'}
                </span>
              </>
            )}
          </div>
        </div>
        {/* Action chips, wrapped in their own flex sub-container so
            they all stack onto a SECOND row together on narrow
            screens (rather than each chip wrapping individually,
            which produced a 3-4 row tower on phones). flex-basis
            100% via the outer pill's flex-wrap drops this whole
            block under the title block automatically.

            Each chip has ≥ 28px tap target via padding so the
            cluster is mobile-friendly even when stacked. The Pause
            / Free-Look split is intentional: Pause halts the queue
            advancement entirely (use when you want to read up on
            the current location undisturbed), Free-Look just stops
            the camera (lets you drag without losing your place —
            the queue keeps growing in the bg).

            Host-only: ＋ Add place, ⏸ Pause / ▶ Resume.
            Always visible: 🎥 Follow / ✋ Free Look, Exit. */}
        <div
          style={{
            display:    'flex',
            alignItems: 'center',
            flexWrap:   'wrap',
            gap:        6,
            // Pull right on wide layouts; on narrow layouts the
            // outer pill's flex-wrap drops us under the title and
            // this auto-margin becomes a no-op.
            marginLeft: 'auto',
          }}
        >
        {role !== 'viewer' && (
          <button
            type="button"
            onClick={() => setPlaceOpen((v) => !v)}
            aria-expanded={placeOpen}
            title={placeOpen
              ? 'Close place search.'
              : 'Search for a place (Mapbox + GeoLink cache) and queue it as the next teleport stop.'}
            style={{
              ...buttonBase,
              padding:     '6px 10px',
              fontSize:    11,
              background:  placeOpen ? 'rgba(99, 102, 241, 0.22)' : 'rgba(99, 102, 241, 0.12)',
              borderColor: 'rgba(99, 102, 241, 0.45)',
              color:       '#c7d2fe',
              whiteSpace:  'nowrap',
            }}
          >
            {placeOpen ? '× Close' : '＋ Add place'}
          </button>
        )}
        {role !== 'viewer' && (
          <button
            type="button"
            onClick={() => setPaused(!paused)}
            aria-pressed={paused}
            title={
              paused
                ? 'Cinematic is paused. The queue still grows but the camera stops auto-advancing. Watchers see "Host paused". Click to resume.'
                : 'Pause the cinematic. Queued stops accumulate but the camera stops auto-advancing — useful when you want to take in a stop without rushing on. Touching the map auto-pauses + enables Free Look so you can drag without losing your spot.'
            }
            style={{
              ...buttonBase,
              padding:     '6px 10px',
              fontSize:    11,
              background:  paused
                ? 'rgba(168, 85, 247, 0.20)'
                : 'rgba(168, 85, 247, 0.10)',
              borderColor: 'rgba(168, 85, 247, 0.45)',
              color:       '#d8b4fe',
              whiteSpace:  'nowrap',
            }}
          >
            {paused ? '▶ Resume' : '⏸ Pause'}
          </button>
        )}
        <button
          type="button"
          onClick={() => setManualOverride(!manualOverride)}
          aria-pressed={manualOverride}
          title={
            manualOverride
              ? 'Free Look is ON — camera is paused. Drag the map freely. Click to resume following the agent.'
              : 'Following the agent automatically. Click to pause and explore the scene yourself (queue keeps growing in the background).'
          }
          style={{
            ...buttonBase,
            padding:     '6px 10px',
            fontSize:    11,
            background:  manualOverride
              ? 'rgba(245, 158, 11, 0.18)'
              : 'rgba(34, 197, 94, 0.16)',
            borderColor: manualOverride
              ? 'rgba(245, 158, 11, 0.45)'
              : 'rgba(34, 197, 94, 0.4)',
            color:       manualOverride ? '#fbbf24' : '#86efac',
            whiteSpace:  'nowrap',
          }}
        >
          {manualOverride ? '✋ Free Look' : '🎥 Follow'}
        </button>
        {isHost && (
          <button
            type="button"
            onClick={() => {
              const next = !autoBroadcastOn;
              setAutoBroadcastOn(next);
              if (next) {
                // Manual kick: an explicit host press starts a
                // broadcast right away (even solo) instead of waiting
                // for the next arrival + a viewer to join. The
                // automatic arrival-driven trigger below still holds
                // off until the session actually has viewers.
                if (viewerWallet && selectedAgentId && autoBroadcast.status === 'idle') {
                  lastBroadcastAtRef.current = Date.now();
                  narrateIdxRef.current = 0;
                  void autoBroadcast.start();
                }
              } else if (autoBroadcast.status !== 'idle') {
                void autoBroadcast.stop('toggle_off');
              }
            }}
            aria-pressed={autoBroadcastOn}
            disabled={!viewerWallet}
            title={
              !viewerWallet
                ? 'Connect your wallet to let the agent auto-broadcast (the live session is paid from your wallet).'
                : autoBroadcastOn
                  ? `Auto-broadcast is ON. The agent does a short narrated live broadcast (~30s) when it arrives somewhere and the session has viewers; each is saved to its profile.${broadcastLive ? ` LIVE ${autoBroadcast.elapsedSec}s · ${autoBroadcast.viewerCount} watching` : ''}`
                  : 'Let the agent occasionally do a short narrated live broadcast during the teleport (reuses the live/SFU stack, saved to the agent profile).'
            }
            style={{
              ...buttonBase,
              padding:     '6px 10px',
              fontSize:    11,
              background:  broadcastLive
                ? 'rgba(239, 68, 68, 0.22)'
                : autoBroadcastOn
                  ? 'rgba(239, 68, 68, 0.14)'
                  : 'rgba(239, 68, 68, 0.08)',
              borderColor: 'rgba(239, 68, 68, 0.45)',
              color:       '#fca5a5',
              opacity:     viewerWallet ? 1 : 0.5,
              cursor:      viewerWallet ? 'pointer' : 'not-allowed',
              whiteSpace:  'nowrap',
            }}
          >
            {broadcastLive
              ? `🔴 LIVE ${autoBroadcast.elapsedSec}s`
              : broadcastBusy
                ? '📡 …'
                : autoBroadcastOn
                  ? '📡 Auto ✓'
                  : '📡 Auto-cast'}
          </button>
        )}
        <button
          type="button"
          onClick={() => exit('user')}
          style={dangerBtn}
          title="Exit teleport mode (Esc)"
        >
          Exit Teleport
        </button>
        </div>
      </div>

      {/* Hidden avatar canvas that backs the agent's auto-broadcast
          MediaStream. Always mounted (cheap when idle) so the engine /
          stream is ready the instant an auto-broadcast fires. */}
      <AgentBroadcastCanvas engine={autoBroadcast.engine} hidden />

      {/* Inline live-broadcast mini player. Mounts only when the
          followed agent has an active LiveSession (status='live').
          Anchored top-right so it doesn't fight the centered control
          pill or the centered countdown / arrival overlays. The
          player is intentionally tiny (~160×90); the user can expand
          or fullscreen it from controls inside the player itself.

          Mounted as its own component so its viewer / engine
          lifecycle is scoped to the broadcast — when the broadcast
          ends or the user follows a different agent, the component
          unmounts and tears down its peer connection cleanly. */}
      {followedAgentLiveSession && (
        <div
          style={{
            position:      'absolute',
            top:           16,
            right:         16,
            zIndex:        2200,
            pointerEvents: 'auto',
            // Keep us out of the way of the right-edge of the
            // centered top pill on wide screens (the pill clamps to
            // `calc(100vw - 32px)` so on a 1280px display it stops
            // around x=1264 — that's well clear of the 160-wide
            // player at top:right:16). On narrow phones the centered
            // pill wraps onto multiple rows so the player's top:16
            // anchor stays uncluttered.
            maxWidth:      'min(calc(100vw - 32px), 320px)',
          }}
        >
          <AgentTeleportLivePlayer
            broadcasterId={followedAgentLiveSession.broadcasterId}
            displayName={selectedAgent?.displayName || null}
            avatarUrl={selectedAgent?.avatarUrl || null}
            viewerWallet={viewerWallet}
            country={followedAgentLiveSession.country}
            hostMap={hostMap}
            hostMapStyle={hostMapStyle}
          />
        </div>
      )}

      {/* Quick map-style chip cluster — its OWN row directly under
          the top pill so it stays close to the action buttons but
          doesn't bloat the chip cluster on narrow screens. Visible
          on every role; each viewer sees their own map's style
          update without affecting the host. */}
      {STYLE_CHIPS.some((c) => c.url) && (
        <div
          style={{
            position:       'absolute',
            top:            76,
            left:           '50%',
            transform:      'translateX(-50%)',
            zIndex:         2010,
            display:        'flex',
            alignItems:     'center',
            gap:            6,
            padding:        '6px 10px',
            background:     'rgba(15, 23, 42, 0.78)',
            border:         '1px solid rgba(99, 102, 241, 0.32)',
            borderRadius:   999,
            backdropFilter: 'blur(6px)',
            // The place-search row also uses top:76; when both are
            // visible we want the place-search dropdown ON TOP, so
            // we'd push the style chips down. That's handled below
            // by `placeOpen` toggling a transform.
            ...(placeOpen
              ? { transform: 'translate(-50%, 64px)', opacity: 0.55 }
              : {}),
            transition: 'transform 200ms ease, opacity 200ms ease',
            pointerEvents: 'auto',
            maxWidth:      'calc(100vw - 32px)',
            flexWrap:      'wrap',
          }}
        >
          <span style={{ font: '600 11px system-ui', color: '#94a3b8', marginRight: 2 }}>Style</span>
          {STYLE_CHIPS.map((chip) => {
            const url = chip.url;
            // Skip the "Original" chip when there's nothing to
            // reset to (no snapshot taken yet — happens before the
            // very first enterMode of the page session if the
            // parent didn't pass `mapStyle`).
            if (!url) return null;
            const isActive = currentMapStyle === url;
            return (
              <button
                key={chip.id}
                type="button"
                onClick={() => setMapStyle(url)}
                title={chip.title}
                aria-pressed={isActive}
                style={{
                  background:   isActive
                    ? 'rgba(99, 102, 241, 0.32)'
                    : 'rgba(99, 102, 241, 0.10)',
                  color:        isActive ? '#e0e7ff' : '#c7d2fe',
                  border:       `1px solid ${isActive ? 'rgba(165, 180, 252, 0.65)' : 'rgba(99, 102, 241, 0.45)'}`,
                  borderRadius: 999,
                  padding:      '5px 11px',
                  font:         '600 11px system-ui, -apple-system, sans-serif',
                  cursor:       isActive ? 'default' : 'pointer',
                  whiteSpace:   'nowrap',
                  transition:   'background 150ms ease, border-color 150ms ease',
                }}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Inline place-search row with TYPEAHEAD — appears below the
          top pill when the user clicks "＋ Add place". Debounced
          (200 ms) Mapbox autocomplete returns up to 5 suggestions
          which render in a click-to-pick dropdown. Picking a row
          calls enqueueLngLat (skipping the cache-first round trip
          since we already have the resolved coords). Pressing Enter
          uses the top suggestion if one is loaded, otherwise falls
          through to enqueuePlace's full cache-first flow. Country
          hint comes from the selected agent so "Springfield"
          disambiguates correctly within the agent's country. */}
      {placeOpen && role !== 'viewer' && (
        <div
          style={{
            ...cardStyle,
            // Sits BELOW the style-chip row (which lives at top: 76)
            // so the two don't fight for the same vertical band on
            // narrow screens. The style-chip cluster also dims +
            // shifts down when placeOpen is true (see above) to
            // avoid visual collisions on the first render before
            // this row is fully painted.
            top:           130,
            left:          '50%',
            transform:     'translateX(-50%)',
            minWidth:      280,
            // On narrow phones we stretch nearly to the viewport
            // edges so the suggestions are tappable; on desktop the
            // 480px cap keeps the dropdown readable.
            maxWidth:      'min(calc(100vw - 16px), 480px)',
            width:         'min(calc(100vw - 16px), 480px)',
            padding:       '10px 12px',
            border:        '1px solid rgba(99, 102, 241, 0.5)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span aria-hidden style={{ fontSize: 14 }}>📍</span>
            <input
              ref={placeInputRef}
              type="text"
              value={placeQuery}
              onChange={(e) => setPlaceQuery(e.target.value)}
              onFocus={() => setPlaceFocused(true)}
              onBlur={() => {
                // Defer hide so a click on a suggestion fires before
                // the dropdown disappears (otherwise blur swallows
                // the click). 150 ms matches Mapbox's own search box.
                setTimeout(() => setPlaceFocused(false), 150);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void submitPlace();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setPlaceOpen(false);
                  setPlaceQuery('');
                }
              }}
              disabled={placeBusy}
              placeholder={selectedAgent?.country_code
                ? `Search a place in ${selectedAgent.country_code}…`
                : 'Search a place to teleport to…'}
              aria-label="Search a place to add to the teleport queue"
              style={{
                flex:         1,
                minWidth:     0,
                background:   'rgba(15, 23, 42, 0.85)',
                color:        '#e2e8f0',
                border:       '1px solid rgba(148, 163, 184, 0.3)',
                borderRadius: 6,
                padding:      '8px 10px',
                font:         '500 13px system-ui, -apple-system, sans-serif',
                outline:      'none',
              }}
            />
            <button
              type="button"
              onClick={() => void submitPlace()}
              disabled={placeBusy || !placeQuery.trim()}
              style={{
                ...primaryBtn,
                opacity:     (placeBusy || !placeQuery.trim()) ? 0.55 : 1,
                cursor:      (placeBusy || !placeQuery.trim()) ? 'default' : 'pointer',
                padding:     '8px 14px',
                whiteSpace:  'nowrap',
              }}
            >
              {placeBusy ? 'Searching…' : 'Queue'}
            </button>
          </div>

          {/* Autocomplete dropdown. Only show when the input is
              focused AND we have suggestions — keeps the dropdown
              from lingering after the user picks a result and we
              clear the query (which would otherwise show "no
              results yet" UX noise). */}
          {placeFocused && placeSuggestions.length > 0 && (
            <ul
              role="listbox"
              aria-label="Place suggestions"
              style={{
                listStyle:    'none',
                margin:       '8px 0 0 0',
                padding:      0,
                background:   'rgba(2, 6, 23, 0.95)',
                border:       '1px solid rgba(99, 102, 241, 0.35)',
                borderRadius: 8,
                maxHeight:    260,
                overflowY:    'auto',
              }}
            >
              {placeSuggestions.map((s) => (
                <li
                  key={s.id}
                  role="option"
                  // We don't use aria-selected because we don't
                  // track a focused row index — it's a click/tap
                  // dropdown, not keyboard-arrow nav (Enter still
                  // picks the TOP row via submitPlace). Future
                  // improvement: wire arrow keys → focus index.
                  aria-selected={false}
                  onMouseDown={(e) => {
                    // mousedown beats blur — without this the input's
                    // blur handler would close the dropdown before
                    // our click fires.
                    e.preventDefault();
                    pickSuggestion(s);
                  }}
                  style={{
                    padding:    '10px 12px',
                    cursor:     'pointer',
                    color:      '#e2e8f0',
                    font:       '500 12.5px system-ui, -apple-system, sans-serif',
                    borderBottom: '1px solid rgba(99, 102, 241, 0.12)',
                    transition: 'background 120ms ease',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(99, 102, 241, 0.18)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                  }}
                >
                  <div>{s.placeName}</div>
                  {s.country && (
                    <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>
                      {s.country} · {s.lat.toFixed(3)}, {s.lng.toFixed(3)}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Place-search result toast — 4s auto-dismiss. Distinct from
          the role-change toast (which lives at top: 80) so the two
          can't visually collide if the host is just being promoted
          and queues a place in the same beat. */}
      {placeToast && (
        <div
          role="status"
          style={{
            position:      'absolute',
            top:           placeOpen ? 132 : 76,
            left:          '50%',
            transform:     'translateX(-50%)',
            zIndex:        2300,
            background:    'rgba(15, 23, 42, 0.96)',
            color:         placeToast.kind === 'ok' ? '#86efac' : '#fca5a5',
            border:        `1px solid ${placeToast.kind === 'ok' ? 'rgba(34, 197, 94, 0.5)' : 'rgba(239, 68, 68, 0.5)'}`,
            borderRadius:  10,
            padding:       '6px 12px',
            font:          '600 11.5px system-ui, -apple-system, sans-serif',
            boxShadow:     '0 4px 16px rgba(0, 0, 0, 0.45)',
            pointerEvents: 'none',
            maxWidth:      'calc(100vw - 32px)',
            animation:     'gt-teleport-toast-in 240ms ease-out',
          }}
        >
          {placeToast.text}
        </div>
      )}

      {roleToast && (
        <div
          role="status"
          style={{
            position:      'absolute',
            top:           80,
            left:          '50%',
            transform:     'translateX(-50%)',
            zIndex:        2300,
            background:    'rgba(15, 23, 42, 0.96)',
            color:         '#fef3c7',
            border:        '1px solid rgba(251, 191, 36, 0.5)',
            borderRadius:  10,
            padding:       '8px 14px',
            font:          '600 12px system-ui, -apple-system, sans-serif',
            boxShadow:     '0 6px 24px rgba(0, 0, 0, 0.45)',
            pointerEvents: 'none',
            animation:     'gt-teleport-toast-in 240ms ease-out',
          }}
        >
          {roleToast}
        </div>
      )}

      {/* (The Follow / Free-Look toggle used to live as a separate
          bottom-left chip here. Moved into the top-center pill so
          it's reachable on mobile, where the bottom-left was
          covered by the player marker controls and the country
          selector.) */}

      {/* Countdown overlay — only during the countdown phase. */}
      {phase === 'countdown' && countdownSeconds != null && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2300,
            pointerEvents: 'none',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              font: '800 96px/1 system-ui, -apple-system, sans-serif',
              color: '#f59e0b',
              textShadow: '0 4px 24px rgba(0, 0, 0, 0.7)',
              transition: 'transform 200ms ease',
              transform:  'scale(1.1)',
            }}
          >
            {countdownSeconds}
          </div>
          <div
            style={{
              marginTop: 6,
              font: '600 14px system-ui, -apple-system, sans-serif',
              color: '#fde68a',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.7)',
            }}
          >
            Next teleport
          </div>
          {/* Show the destination name (or coords fallback) under
              the countdown so the user knows WHERE they're about to
              be taken before the flight starts. Two-tier hierarchy
              matches the Arrived overlay: big primary line (city /
              locality) + smaller secondary line ("State · Country").
              Reverse-geocoded by the hook on enqueue; manually-
              added places already have their place_name primed in
              the cache. */}
          {(nextStopName || nextDestination) && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  font:         '800 26px/1.25 system-ui, -apple-system, sans-serif',
                  color:        '#fef3c7',
                  textShadow:   '0 2px 12px rgba(0, 0, 0, 0.8)',
                  letterSpacing: 0.2,
                  maxWidth:     'min(86vw, 580px)',
                  marginLeft:   'auto',
                  marginRight:  'auto',
                  wordBreak:    'break-word',
                }}
              >
                {nextStopName?.short || _coordLabel(nextDestination)}
              </div>
              {(nextStopName?.region || nextStopName?.country) && (
                <div
                  style={{
                    marginTop:   4,
                    font:        '600 15px/1.2 system-ui, -apple-system, sans-serif',
                    color:       '#fde68a',
                    textShadow:  '0 2px 10px rgba(0, 0, 0, 0.7)',
                    letterSpacing: 0.4,
                  }}
                >
                  {[nextStopName.region, nextStopName.country].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Nearby markers panel — anchored bottom-left so it doesn't
          fight the centered countdown / arrival overlays. Lists the
          smart-contract execution-rule markers + NFT markers within
          ~750 m of the agent, plus any OTHER live broadcasters
          streaming within ~20 km of the scene. Deliberately omits
          wallets per the product brief ("don't announce nearby
          wallets"); the GeoLink query only requests contracts + nfts
          so nothing else is even transmitted. Visible throughout the
          whole cinematic (entering / flying / arrived / rotating /
          countdown) so in-scene markers are detected while the
          teleport is in progress, not only once parked. */}
      {((nearby
          && nearby.address === selectedAgentId
          && (nearby.contracts.length > 0 || nearby.nfts.length > 0))
        || nearbyLive.length > 0
        || (spatialNearby && spatialNearby.address === selectedAgentId && spatialNearby.objects.length > 0)) && (
        <div
          style={{
            position:      'absolute',
            bottom:        16,
            left:          16,
            zIndex:        2200,
            pointerEvents: 'auto',
            maxWidth:      'min(calc(100vw - 32px), 320px)',
            background:    'rgba(15, 23, 42, 0.92)',
            color:         '#e2e8f0',
            border:        '1px solid rgba(99, 102, 241, 0.35)',
            borderRadius:  12,
            padding:       '10px 12px',
            boxShadow:     '0 6px 24px rgba(0, 0, 0, 0.45)',
            font:          '500 12px/1.4 system-ui, -apple-system, sans-serif',
            backdropFilter: 'blur(6px)',
          }}
        >
          <div
            style={{
              display:       'flex',
              alignItems:    'center',
              gap:           6,
              marginBottom:  8,
              fontWeight:    700,
              fontSize:      11,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              color:         '#a5b4fc',
            }}
          >
            <span aria-hidden style={{ fontSize: 13 }}>📍</span>
            Nearby in scene
            {nearby && (
              <span style={{ marginLeft: 'auto', color: '#94a3b8', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
                ~{nearby.radius_m} m
              </span>
            )}
          </div>

          {/* Live broadcasters in range — shown first because a live
              stream in the scene is the most time-sensitive thing to
              surface. Uses the quantised (~11 km) approx location, so
              distances are rounded to whole km and the radius is
              generous (20 km). */}
          {nearbyLive.length > 0 && (
            <div style={{ marginBottom: (nearby && (nearby.contracts.length > 0 || nearby.nfts.length > 0)) ? 8 : 0 }}>
              <div style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Live now ({nearbyLive.length})
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {nearbyLive.slice(0, 6).map((l) => (
                  <li
                    key={l.broadcasterId}
                    title={l.wallet || l.broadcasterId}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 12 }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width:        7,
                        height:       7,
                        borderRadius: '50%',
                        background:   '#ef4444',
                        boxShadow:    '0 0 6px #ef4444',
                        flexShrink:   0,
                      }}
                    />
                    <span
                      style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {shortPk(l.wallet || l.broadcasterId)}
                    </span>
                    <span style={{ color: '#94a3b8', fontSize: 10.5, fontVariantNumeric: 'tabular-nums' }}>
                      ~{Math.max(1, Math.round(l.distanceKm))} km
                    </span>
                  </li>
                ))}
              </ul>
              {nearbyLive.length > 6 && (
                <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 2 }}>
                  +{nearbyLive.length - 6} more
                </div>
              )}
            </div>
          )}
          {/* Spatial objects (registry) the agent is passing — each
              tagged with its type so a viewer can tell a building
              from an event from a contract trigger. Sorted closest
              first; capped at 6 with a "+N more" footer. */}
          {spatialNearby && spatialNearby.address === selectedAgentId && spatialNearby.objects.length > 0 && (
            <div style={{ marginBottom: (nearby && (nearby.contracts.length > 0 || nearby.nfts.length > 0)) ? 8 : 0 }}>
              <div style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Spatial objects ({spatialNearby.objects.length})
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {spatialNearby.objects.slice(0, 6).map((o) => {
                  const meta = spatialTypeMeta(o.type);
                  return (
                    <li
                      key={o.id}
                      title={`${meta.label} · ${o.id}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 12 }}
                    >
                      <span aria-hidden style={{ flexShrink: 0 }}>{meta.icon}</span>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {o.name}
                        <span style={{ color: '#64748b', fontWeight: 500 }}> · {meta.label}</span>
                      </span>
                      <span style={{ color: '#94a3b8', fontSize: 10.5, fontVariantNumeric: 'tabular-nums' }}>
                        {formatDistance(o.distanceM)}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {spatialNearby.objects.length > 6 && (
                <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 2 }}>
                  +{spatialNearby.objects.length - 6} more
                </div>
              )}
            </div>
          )}

          {nearby && nearby.contracts.length > 0 && (
            <div style={{ marginBottom: nearby.nfts.length > 0 ? 8 : 0 }}>
              <div style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Smart contracts ({nearby.contracts.length})
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {nearby.contracts.slice(0, 6).map((c) => (
                  <li
                    key={c.contract_id}
                    title={c.contract_id}
                    style={{
                      display:      'flex',
                      alignItems:   'center',
                      gap:          6,
                      padding:      '3px 0',
                      fontSize:     12,
                    }}
                  >
                    <span aria-hidden style={{ color: '#8b5cf6' }}>◆</span>
                    <span
                      style={{
                        flex:         1,
                        minWidth:     0,
                        overflow:     'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace:   'nowrap',
                      }}
                    >
                      {c.displayName || shortPk(c.contract_id)}
                    </span>
                    {Number.isFinite(c.distance_m) && (
                      <span style={{ color: '#94a3b8', fontSize: 10.5, fontVariantNumeric: 'tabular-nums' }}>
                        {Math.round(c.distance_m)} m
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              {nearby.contracts.length > 6 && (
                <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 2 }}>
                  +{nearby.contracts.length - 6} more
                </div>
              )}
            </div>
          )}
          {nearby && nearby.nfts.length > 0 && (
            <div>
              <div style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                NFTs ({nearby.nfts.length})
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {nearby.nfts.slice(0, 6).map((n) => (
                  <li
                    key={n.nft_id}
                    title={n.nft_id}
                    style={{
                      display:      'flex',
                      alignItems:   'center',
                      gap:          6,
                      padding:      '3px 0',
                      fontSize:     12,
                    }}
                  >
                    <span aria-hidden style={{ color: '#f472b6' }}>★</span>
                    <span
                      style={{
                        flex:         1,
                        minWidth:     0,
                        overflow:     'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace:   'nowrap',
                      }}
                    >
                      {n.name || shortPk(n.nft_id)}
                    </span>
                    {Number.isFinite(n.distance_m) && (
                      <span style={{ color: '#94a3b8', fontSize: 10.5, fontVariantNumeric: 'tabular-nums' }}>
                        {Math.round(n.distance_m)} m
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              {nearby.nfts.length > 6 && (
                <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 2 }}>
                  +{nearby.nfts.length - 6} more
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Arrival overlay — small celebratory pulse. */}
      {phase === 'arrived' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2250,
            pointerEvents: 'none',
            textAlign: 'center',
            color: '#10b981',
            font: '800 28px/1.2 system-ui, -apple-system, sans-serif',
            textShadow: '0 3px 12px rgba(0, 0, 0, 0.6)',
            opacity: 0.9,
            transition: 'opacity 400ms ease',
          }}
        >
          ✦ Arrived
          {/* Show the city / locality the camera just landed at when
              reverse-geocoding has resolved it; fall back to coords
              while the lookup is in flight. Two-tier hierarchy:
              big primary line (city / locality) + smaller secondary
              line ("State · Country") — easier to read at a glance
              even while the camera is still settling and the
              overlay starts fading. */}
          {(currentStopName || nextDestination) && (
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  font:         '800 28px/1.2 system-ui, -apple-system, sans-serif',
                  color:        '#d1fae5',
                  textShadow:   '0 3px 14px rgba(0, 0, 0, 0.75)',
                  letterSpacing: 0.2,
                  maxWidth:     'min(86vw, 580px)',
                  marginLeft:   'auto',
                  marginRight:  'auto',
                  wordBreak:    'break-word',
                }}
              >
                {currentStopName?.short || _coordLabel(nextDestination)}
              </div>
              {(currentStopName?.region || currentStopName?.country) && (
                <div
                  style={{
                    marginTop:   4,
                    font:        '600 16px/1.2 system-ui, -apple-system, sans-serif',
                    color:       '#a7f3d0',
                    textShadow:  '0 2px 10px rgba(0, 0, 0, 0.7)',
                    letterSpacing: 0.4,
                  }}
                >
                  {[currentStopName.region, currentStopName.country].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  ) : null;

  if (!selectionPanel && !activeHUD) return null;
  return (
    <>
      {selectionPanel}
      {activeHUD}
    </>
  );
};

export default AgentTeleportHUD;
